import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { resource } from '../utils/resource/old';

const FAVORITES_STORAGE_KEY = 'divinea_favorites';

@Injectable({
  providedIn: 'root'
})
export class FavoritesService {

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /**
   * Check if we're running in the browser
   */
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Safely get item from localStorage
   */
  private getLocalStorageItem(key: string): string | null {
    if (!this.isBrowser()) {
      return null;
    }
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  }

  /**
   * Safely get item from sessionStorage
   */
  private getSessionStorageItem(key: string): string | null {
    if (!this.isBrowser()) {
      return null;
    }
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error(`Error reading ${key} from sessionStorage:`, error);
      return null;
    }
  }

  /**
   * Safely set item in localStorage
   */
  private setLocalStorageItem(key: string, value: string): void {
    if (!this.isBrowser()) {
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  }

  /**
   * Check if user is logged in by checking for auth token in localStorage
   */
  private isUserLoggedIn(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    // Check for common token storage keys
    const token = this.getLocalStorageItem('auth_token') || 
                  this.getLocalStorageItem('token') || 
                  this.getLocalStorageItem('access_token') ||
                  this.getSessionStorageItem('auth_token') ||
                  this.getSessionStorageItem('token');
    return !!token;
  }

  /**
   * Get user token from storage
   */
  private getUserToken(): string | null {
    if (!this.isBrowser()) {
      return null;
    }
    return this.getLocalStorageItem('auth_token') || 
           this.getLocalStorageItem('token') || 
           this.getLocalStorageItem('access_token') ||
           this.getSessionStorageItem('auth_token') ||
           this.getSessionStorageItem('token');
  }

  /**
   * Get favorites from localStorage (for anonymous users)
   */
  private getLocalStorageFavorites(): string[] {
    if (!this.isBrowser()) {
      return [];
    }
    try {
      const favoritesJson = this.getLocalStorageItem(FAVORITES_STORAGE_KEY);
      if (favoritesJson) {
        return JSON.parse(favoritesJson);
      }
    } catch (error) {
      console.error('Error reading favorites from localStorage:', error);
    }
    return [];
  }

  /**
   * Save favorites to localStorage (for anonymous users)
   */
  private saveLocalStorageFavorites(experienceIds: string[]): void {
    if (!this.isBrowser()) {
      return;
    }
    try {
      this.setLocalStorageItem(FAVORITES_STORAGE_KEY, JSON.stringify(experienceIds));
    } catch (error) {
      console.error('Error saving favorites to localStorage:', error);
    }
  }

  /**
   * Get favorites from backend (for logged-in users)
   */
  private getBackendFavorites(): Observable<string[]> {
    const token = this.getUserToken();
    if (!token) {
      return throwError(() => new Error('No authentication token found'));
    }

    return resource('divineaCom://user/favorites')
      .bearer(token)
      .get<string[]>()
      .pipe(
        catchError(error => {
          console.error('Error fetching favorites from backend:', error);
          // Fallback to empty array if backend call fails
          return of([]);
        })
      );
  }

  /**
   * Add favorite to backend (for logged-in users)
   */
  private addBackendFavorite(experienceId: string): Observable<boolean> {
    const token = this.getUserToken();
    if (!token) {
      return throwError(() => new Error('No authentication token found'));
    }

    return resource('divineaCom://user/favorites')
      .bearer(token)
      .post<{ success: boolean }>({ experienceId })
      .pipe(
        map(response => response?.success ?? true),
        catchError(error => {
          console.error('Error adding favorite to backend:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Remove favorite from backend (for logged-in users)
   */
  private removeBackendFavorite(experienceId: string): Observable<boolean> {
    const token = this.getUserToken();
    if (!token) {
      return throwError(() => new Error('No authentication token found'));
    }

    return resource(`divineaCom://user/favorites/${experienceId}`)
      .bearer(token)
      .delete()
      .pipe(
        map(() => true),
        catchError(error => {
          console.error('Error removing favorite from backend:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get all favorite experience IDs
   */
  getFavorites(): Observable<string[]> {
    if (this.isUserLoggedIn()) {
      return this.getBackendFavorites();
    } else {
      return of(this.getLocalStorageFavorites());
    }
  }

  /**
   * Check if an experience is favorited
   */
  isFavorite(experienceId: string): Observable<boolean> {
    if (this.isUserLoggedIn()) {
      return this.getBackendFavorites().pipe(
        map(favorites => favorites.includes(experienceId))
      );
    } else {
      const favorites = this.getLocalStorageFavorites();
      return of(favorites.includes(experienceId));
    }
  }

  /**
   * Toggle favorite status for an experience
   */
  toggleFavorite(experienceId: string): Observable<boolean> {
    if (this.isUserLoggedIn()) {
      // For logged-in users, check current status first, then toggle
      return this.isFavorite(experienceId).pipe(
        switchMap(isFav => {
          if (isFav) {
            // Remove from favorites
            return this.removeBackendFavorite(experienceId).pipe(
              map(() => false),
              catchError(error => {
                console.error('Error removing favorite:', error);
                return of(false); // Return false on error to indicate removal failed
              })
            );
          } else {
            // Add to favorites
            return this.addBackendFavorite(experienceId).pipe(
              map(() => true),
              catchError(error => {
                console.error('Error adding favorite:', error);
                return of(false); // Return false on error to indicate addition failed
              })
            );
          }
        })
      );
    } else {
      // For anonymous users, use localStorage
      const favorites = this.getLocalStorageFavorites();
      const index = favorites.indexOf(experienceId);
      
      if (index > -1) {
        // Remove from favorites
        favorites.splice(index, 1);
        this.saveLocalStorageFavorites(favorites);
        return of(false);
      } else {
        // Add to favorites
        favorites.push(experienceId);
        this.saveLocalStorageFavorites(favorites);
        return of(true);
      }
    }
  }

  /**
   * Add experience to favorites
   */
  addFavorite(experienceId: string): Observable<boolean> {
    if (this.isUserLoggedIn()) {
      return this.addBackendFavorite(experienceId);
    } else {
      const favorites = this.getLocalStorageFavorites();
      if (!favorites.includes(experienceId)) {
        favorites.push(experienceId);
        this.saveLocalStorageFavorites(favorites);
      }
      return of(true);
    }
  }

  /**
   * Remove experience from favorites
   */
  removeFavorite(experienceId: string): Observable<boolean> {
    if (this.isUserLoggedIn()) {
      return this.removeBackendFavorite(experienceId);
    } else {
      const favorites = this.getLocalStorageFavorites();
      const index = favorites.indexOf(experienceId);
      if (index > -1) {
        favorites.splice(index, 1);
        this.saveLocalStorageFavorites(favorites);
      }
      return of(true);
    }
  }
}
